'use client'

import type React from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'

interface SuggestedQuestionsProps {
  agentType: string
  onQuestionClick: (prompt: string) => Promise<void>
}

export function SuggestedQuestions({ agentType, onQuestionClick }: SuggestedQuestionsProps) {
  const { t } = useTranslation()

  const handleQuestionClick = async (promptKey: string, questionName: string) => {
    const prompt = t(promptKey)
    if (prompt && prompt.trim()) {
      await onQuestionClick(prompt)
    } else {
      console.error(`[SuggestedQuestions] Empty prompt for ${questionName}`)
    }
  }

  const chipStyle = {
    borderRadius: '20px',
    bgcolor: '#E0E0E0',
    color: 'text.primary',
    '&:hover': {
      bgcolor: '#D0D0D0',
    },
    cursor: 'pointer',
  }

  return (
    <Box sx={{ mb: 3, width: '100%', maxWidth: 600, mx: 'auto' }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1.5,
          color: 'text.secondary',
          fontSize: '0.75rem',
        }}
      >
        {t('page.medconnectAgent.chatInterface.suggestedQuestions')}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {agentType === 'expertise' ? (
          // Questions pour le contexte d'expertise
          <>
            <Chip
              clickable
              label={t('page.medconnectAgent.chatInterface.suggestedQuestion.expertise.summarizePatientInfo.label')}
              onClick={() =>
                handleQuestionClick('page.medconnectAgent.chatInterface.suggestedQuestion.expertise.summarizePatientInfo.prompt', 'summarizePatientInfo')
              }
              sx={chipStyle}
            />
            <Chip
              clickable
              label={t('page.medconnectAgent.chatInterface.suggestedQuestion.expertise.previousExpertises.label')}
              onClick={() =>
                handleQuestionClick('page.medconnectAgent.chatInterface.suggestedQuestion.expertise.previousExpertises.prompt', 'previousExpertises')
              }
              sx={chipStyle}
            />
            <Chip
              clickable
              label={t('page.medconnectAgent.chatInterface.suggestedQuestion.expertise.patientStatus.label')}
              onClick={() => handleQuestionClick('page.medconnectAgent.chatInterface.suggestedQuestion.expertise.patientStatus.prompt', 'patientStatus')}
              sx={chipStyle}
            />
          </>
        ) : (
          // Questions pour le contexte normal (Elara utilities)
          <>
            <Chip
              clickable
              label={t('page.medconnectAgent.chatInterface.suggestedQuestion.normal.explainDisease.label')}
              onClick={() => handleQuestionClick('page.medconnectAgent.chatInterface.suggestedQuestion.normal.explainDisease.prompt', 'explainDisease')}
              sx={chipStyle}
            />
            <Chip
              clickable
              label={t('page.medconnectAgent.chatInterface.suggestedQuestion.normal.elaraUtilities.label')}
              onClick={() => handleQuestionClick('page.medconnectAgent.chatInterface.suggestedQuestion.normal.elaraUtilities.prompt', 'elaraUtilities')}
              sx={chipStyle}
            />
            <Chip
              clickable
              label={t('page.medconnectAgent.chatInterface.suggestedQuestion.normal.medicalGuidance.label')}
              onClick={() => handleQuestionClick('page.medconnectAgent.chatInterface.suggestedQuestion.normal.medicalGuidance.prompt', 'medicalGuidance')}
              sx={chipStyle}
            />
          </>
        )}
      </Stack>
    </Box>
  )
}

export default SuggestedQuestions
